import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import "./App.css";
import { PageLoader } from "./components/common/pageLoader";
import { Navigate, Route, Routes } from "react-router-dom";
import { Error } from "./pages/error";
import { Sessions } from "./pages/sessions/sessions";
import { ThemeProvider } from "@mui/material/styles";
import useTheme from "./hooks/useTheme";
import createTheme from "./theme";
import { LicenseInfo } from "@mui/x-license-pro";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import DateFnsUtils from "@date-io/date-fns";
import Resources from "./pages/resources/resources";
import Registrations from "./pages/registrations/registrations";
import { ProtectedRoute } from "./components/common/protected-route";
import SessionDetail from "./pages/sessions/sessionDetail/sessionDetail";
import ResourceDetail from "./pages/resources/resourceDetail/resourceDetail";
import Users from "./pages/users/users";
import TrainerEntryDetail from "./pages/sessions/sessionDetail/payroll/trainerEntryDetail/trainerEntryDetail";
import Home from "./pages/home/home";
import MyAccess from "./pages/my-access/myaccess";
import Notifications from "./pages/notifications/notifications";
import NotificationDetail from "./pages/notifications/notificationDetail/notificationDetail";
import RegistrationDetail from "./pages/registrations/registrationDetail/registrationDetail";
import TemplatePage from "./pages/emailTemplates/emailTemplatePage";
import PartnerDetail from "./pages/resources/partnerDetail/partnerDetail";
import { updateSessionsGridState } from "./app/slices/sessionListTableStateSlice";
import { useLazyGetUserPreferencesByEmailQuery } from "./services/cclTokenedUserPreferenceApi";
import { UserClaimsService, eGroupIds } from "./services/currentUserService/currentUserService";
import { UserPreference } from "./services/types/userPreferenceTypes";
import { useDispatch, useSelector } from "react-redux";
import { addUserClaims } from "./app/slices/userClaimsSlice";
import { updateResourceGridState } from "./app/slices/resourceListTableStateSlice";
import { updateRegistrationGridState } from "./app/slices/registrationListTableStateSlice";
import { updateNotificationsGridState } from "./app/slices/notificationListTableStateSlice";
import Applications from "./pages/applications/applications";
import { updateUserSearchGridState } from "./app/slices/userSearchListTableStateSlice";
import UserDetail from "./pages/users/userDetail/userDetail";
import { AllowedRoutesState, updateAllowedRoutes } from "./app/slices/allowedRoutesSlice";
import { updateProductSaleGridState } from "./app/slices/productSaleListTableStateSlice";
import { updateAssignmentGridState } from "./app/slices/assignmentListTableStateSlice";
import AccessLogs from "./pages/accesslogs/accessLogs";
import ApplicationDetail from "./pages/applications/applicationDetail/applicationDetail";
import { updateAccessEventListGridState } from "./app/slices/accessEventListTableStateSlice";
import SessionTimeOut from "./components/common/sessionTimeOut";
import { updatePartnerResourceGridState } from "./app/slices/partnerResourceListTableStateSlice";
import { updateApplicationUsersSearchGridState } from "./app/slices/applicationUsersSearchListTableStateSlice";
import { updateApplicationRolesSearchGridState } from "./app/slices/applicationRolesSearchListTableStateSlice";
import WorkPoolDetail from "./pages/resources/workPools/workPoolDetails/workPoolDetail";
import { updateWorkPoolResourceGridState } from "./app/slices/workPoolsResourceListTableStateSlice";
import { updateApplicationsSearchGridState } from "./app/slices/applicationSearchListTableStateSlice";
import { registerLicense } from "@syncfusion/ej2-base";
import SystemDetail from "./pages/applications/systemDetail/systemDetail";
import { updateBiosGridState } from "./app/slices/resourceBioSearchGridSlice";
import { updateAttributesGridState } from "./app/slices/resourceAttributesSearchListSlice";
import { updateContractsGridState } from "./app/slices/resourceContractsSearchListSlice";
import RedirectToEmail from "./pages/notifications/notificationDetail/RedirectToEmail";
import EmailTemplateDetail from "./pages/emailTemplates/EmailTemplateDetails/emailTemplateDetailsPage";
import Assessments from "./pages/assessments/assessments";
import GroupReportDetail from "./pages/assessments/groupReports/groupReportsDetails/groupReportDetailPage";
import Clients from "./pages/clients/clients";
import Inventory from "./pages/inventory/inventory";
import OrderDetail from "./pages/inventory/orderDetail";
import { updateScoreReportDownloadGridState } from "./app/slices/scoreReportDownloadListTableStateSlice";
import { updateApplicationInvitationsSearchGridState } from "./app/slices/applicationInvitationsSearchListTableStateSlice";
import {
    loadAreaPreferences,
    AREA_PREFERENCE_TYPE_IDS,
    AREA_PREFERENCE_DEFAULTS,
    deepMergeDefaults,
} from "./app/slices/areaPreferencesSlice";
import { loadRecentActivity } from "./app/slices/recentActivitySlice";
import { useLazyGetRecentActivityQuery } from "./services/cclTokenedAccessEventsApi";
import { useLazyGetPkeyByImKeyQuery } from "./services/cclTokenedEnterpriseCoachingApi";
import { useLazyGetCurrentUserNameQuery } from "./services/cclTokenedEnterpriseParticipantApi";
import { FeatureFlagProvider } from "./featureFlags";
import Documents from "./pages/documents/documents";
import Payroll from "./pages/payroll/payroll";
import MiscWorkDetails from "./pages/payroll/miscWorkDetails/miscWorkDetails";

export const App: React.FC = () => {
    const allowedRoutes: AllowedRoutesState = useSelector((state: any) => state.allowedRoutes);

    //MUIX License
    LicenseInfo.setLicenseKey(
        "bf406dad55ab35713a830a1f9d41bbdaTz0xMjUxMDEsRT0xODAxNDM5OTk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y",
    );

    // Registering Syncfusion license key
    registerLicense(
        "ORg4AjUWIQA/Gnt2VlhhQlJCfV5AQmBIYVp/TGpJfl96cVxMZVVBJAtUQF1hSn9SdkxiWn1ecHJVQGNY",
    );

    // eslint-disable-next-line
    const claimsService = React.useMemo(() => new UserClaimsService(), []);
    const { error, isLoading, isAuthenticated, getIdTokenClaims } = useAuth0();

    const [defaultPage, setDefaultPage] = React.useState<string>("");
    const [unlock, setUnlock] = React.useState<boolean>(false);

    const [getUserPreferences] = useLazyGetUserPreferencesByEmailQuery();
    const [getRecentActivity] = useLazyGetRecentActivityQuery();
    const [getPkeyByImKey] = useLazyGetPkeyByImKeyQuery();
    const [getCurrentUserName] = useLazyGetCurrentUserNameQuery();
    const { theme } = useTheme();
    const dispatch = useDispatch();

    React.useEffect(() => {
        if (!isAuthenticated) return;

        getIdTokenClaims().then((claims) => {
            const claimsObj = claimsService.StoreToken(claims?.__raw as string);
            if (claimsObj != null) {
                dispatch(addUserClaims(claimsObj));

                getCurrentUserName().then((result) => {
                    if (result.data) {
                        claimsService.StoreCurrentUserName(result.data.firstName, result.data.lastName);
                    }
                });

                const routes = claimsService.GetCurrentAllowedRoutes();
                if (routes != null) {
                    dispatch(updateAllowedRoutes(routes));
                }

                getUserPreferences(claimsObj.email)
                    .unwrap()
                    .then((userPrefs) => {
                        let up: UserPreference | undefined = userPrefs.find(
                            (p) => p.userPreferenceTypeId === 1,
                        );
                        // save session table preference to store
                        dispatch(updateSessionsGridState(up?.data ?? null));

                        // save resource table preference to store
                        up = userPrefs.find((p) => p.userPreferenceTypeId === 2);
                        dispatch(updateRegistrationGridState(up?.data ?? null));

                        // save notification table preference to store
                        up = userPrefs.find((p) => p.userPreferenceTypeId === 3);
                        dispatch(updateNotificationsGridState(up?.data ?? null));

                        // save resource table preference to store
                        up = userPrefs.find((p) => p.userPreferenceTypeId === 4);
                        dispatch(updateResourceGridState(up?.data ?? null));

                        // save enterprise user table preference to store
                        up = userPrefs.find((p) => p.userPreferenceTypeId === 6);
                        dispatch(updateUserSearchGridState(up?.data ?? null));

                        // save applications table preference to store
                        up = userPrefs.find((p) => p.userPreferenceTypeId === 7);
                        dispatch(updateApplicationsSearchGridState(up?.data ?? null));

                        // save assignments table preference to store
                        up = userPrefs.find((p) => p.userPreferenceTypeId === 8);
                        dispatch(updateAssignmentGridState(up?.data ?? null));

                        // save product sales table preference to store
                        up = userPrefs.find((p) => p.userPreferenceTypeId === 9);
                        dispatch(updateProductSaleGridState(up?.data ?? null));

                        // save log table preference to store
                        up = userPrefs.find((p) => p.userPreferenceTypeId === 10);
                        dispatch(updateAccessEventListGridState(up?.data ?? null));

                        // save partner resource table preference to store
                        up = userPrefs.find((p) => p.userPreferenceTypeId === 11);
                        dispatch(updatePartnerResourceGridState(up?.data ?? null));

                        // save application user table preference to store
                        up = userPrefs.find((p) => p.userPreferenceTypeId === 12);
                        dispatch(updateApplicationUsersSearchGridState(up?.data ?? null));

                        // save resource bio list grid to store
                        up = userPrefs.find((p) => p.userPreferenceTypeId === 13);
                        dispatch(updateBiosGridState(up?.data ?? null));

                        // save resource attribute grid to store
                        up = userPrefs.find((p) => p.userPreferenceTypeId === 14);
                        dispatch(updateAttributesGridState(up?.data ?? null));

                        // save resource contracts grid to store
                        up = userPrefs.find((p) => p.userPreferenceTypeId === 15);
                        dispatch(updateContractsGridState(up?.data ?? null));

                        // save resource workpools grid to store
                        up = userPrefs.find((p) => p.userPreferenceTypeId === 18);
                        dispatch(updateWorkPoolResourceGridState(up?.data ?? null));

                        // save score report download grid to store
                        up = userPrefs.find((p) => p.userPreferenceTypeId === 19);
                        dispatch(updateScoreReportDownloadGridState(up?.data ?? null));

                        // save application invitation table preference to store
                        up = userPrefs.find((p) => p.userPreferenceTypeId === 20);
                        dispatch(updateApplicationInvitationsSearchGridState(up?.data ?? null));

                        up = userPrefs.find((p) => p.userPreferenceTypeId === 16);
                        if (up != null) {
                            let data = JSON.parse(up?.data);
                            setDefaultPage(data.url ? data.url : "");
                        } else {
                            setDefaultPage("");
                        }
                        setUnlock(true);

                        // save application roles list grid to store
                        up = userPrefs.find((p) => p.userPreferenceTypeId === 17);
                        dispatch(updateApplicationRolesSearchGridState(up?.data ?? null));

                        // load area preferences (merge stored values with defaults)
                        Object.entries(AREA_PREFERENCE_TYPE_IDS).forEach(([area, typeId]) => {
                            const areaPref = userPrefs.find(
                                (p) => p.userPreferenceTypeId === typeId,
                            );
                            const defaults = AREA_PREFERENCE_DEFAULTS[area] ?? {};
                            const stored = areaPref != null ? JSON.parse(areaPref.data) : {};
                            dispatch(
                                loadAreaPreferences({
                                    area,
                                    data: deepMergeDefaults(defaults, stored),
                                }),
                            );
                        });

                        // load recent activity from backend
                        getRecentActivity()
                            .unwrap()
                            .then(async (recentActivityData) => {
                                // Resource URLs require pkey, but events only store imKey.
                                // Fetch pkey for each resource item (max 5, parallel).
                                const enrichedResources = await Promise.all(
                                    recentActivityData.resources.map(async (resource) => {
                                        try {
                                            const result = await getPkeyByImKey(
                                                resource.entityId,
                                            ).unwrap();
                                            return { ...resource, entityId: String(result.pkey) };
                                        } catch {
                                            return resource;
                                        }
                                    }),
                                );
                                dispatch(
                                    loadRecentActivity({
                                        ...recentActivityData,
                                        resources: enrichedResources,
                                    }),
                                );
                            })
                            .catch(() => {
                                // Silently ignore errors - recent activity is non-critical
                            });
                    });
            }
        });
        // eslint-disable-next-line
    }, [isAuthenticated]);

    // richie: this some google analytics code to review and upgrade from useeffects()
    // install('G-32KMZ61TPS'); // install google analytics in head, only runs once per session
    // useEffect(() => {
    //   gtag('send', 'pageview');
    // }, [location]);

    // ---- sample google analytics call ----
    // gtag('event', 'vt-startup', {
    //   title: 'title - vt',
    // })
    const getRootPathElement = (): JSX.Element => {
        if (allowedRoutes.routeFlags.allowHome) {
            if (unlock) {
                if (defaultPage) {
                    return <Navigate to={defaultPage} />;
                } else {
                    return <Navigate to="/home" replace />;
                }
            } else {
                return <ProtectedRoute component={Home} />;
            }
        }

        if (
            allowedRoutes.routeFlags.allowSessions &&
            claimsService.IsUserInGroups([eGroupIds.eClientAdmin, eGroupIds.eNWAAdmin])
        ) {
            return <Navigate to="/sessions" replace />;
        }

        if (allowedRoutes.routeFlags.allowMyAccess) {
            return <Navigate to="/myaccess" replace />;
        }

        return <Navigate to="/resources" replace />;
    };

    if (isLoading && !isAuthenticated) {
        return (
            <div>
                <PageLoader msg={""} />
            </div>
        );
    }

    if (error !== undefined) {
        console.log(error.message);
        return (
            <div>
                <Error />
            </div>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} utils={DateFnsUtils}>
            <ThemeProvider theme={createTheme(theme)}>
                <SessionTimeOut />
                <FeatureFlagProvider isAuthorized={isAuthenticated}>
                    <Routes>
                        {allowedRoutes.routeFlags.allowAssessments && (
                            <Route
                                path="/assessments"
                                element={<ProtectedRoute component={Assessments} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowAssessments && (
                            <Route
                                path="/assessments/:tabIndex"
                                element={<ProtectedRoute component={Assessments} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowAssessments && (
                            <Route
                                path="/assessments/group-reports/:groupReportId"
                                element={<ProtectedRoute component={GroupReportDetail} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowApplications && (
                            <Route
                                path="/applications"
                                element={<ProtectedRoute component={Applications} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowApplications && (
                            <Route
                                path="/system/:systemId"
                                element={<ProtectedRoute component={SystemDetail} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowApplications && (
                            <Route
                                path="/application/:applicationId"
                                element={<ProtectedRoute component={ApplicationDetail} />}
                            />
                        )}
                        <Route path="/error" element={<Error />} />
                        {allowedRoutes.routeFlags.allowNotifications && (
                            <Route path="/notifications" element={<Navigate to="/emails" />} />
                        )}
                        {allowedRoutes.routeFlags.allowNotifications && (
                            <Route
                                path="/emails"
                                element={<ProtectedRoute component={Notifications} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowNotifications && (
                            <Route
                                path="/emails/templates"
                                element={<ProtectedRoute component={TemplatePage} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowNotifications && (
                            <Route
                                path="/emails/templates/:id"
                                element={<ProtectedRoute component={EmailTemplateDetail} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowNotificationDetails && (
                            <Route
                                path="/notification/:notificationId"
                                element={<RedirectToEmail />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowNotificationDetails && (
                            <Route
                                path="/email/:notificationId"
                                element={<ProtectedRoute component={NotificationDetail} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowRegistrations && (
                            <Route
                                path="/registrations"
                                element={<ProtectedRoute component={Registrations} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowRegistrations && (
                            <Route
                                path="/registrations/:searchparam"
                                element={<ProtectedRoute component={Registrations} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowRegistrationDetails && (
                            <Route
                                path="/registration/:esiKey"
                                element={<ProtectedRoute component={RegistrationDetail} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowRegistrationDetails && (
                            <Route
                                path="/registration/:esiKey/:tabIndex"
                                element={<ProtectedRoute component={RegistrationDetail} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowInventory && (
                            <Route
                                path="/inventory"
                                element={<ProtectedRoute component={Inventory} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowInventory && (
                            <Route
                                path="/inventory/:orderNumber"
                                element={<ProtectedRoute component={OrderDetail} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowResources && (
                            <Route
                                path="/resources"
                                element={<ProtectedRoute component={Resources} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowResources && (
                            <Route
                                path="/resources/:target"
                                element={<ProtectedRoute component={Resources} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowResources && (
                            <Route
                                path="/resource/:pKey/:target"
                                element={<ProtectedRoute component={ResourceDetail} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowPayroll && (
                            <Route
                                path="/payroll"
                                element={<ProtectedRoute component={Payroll} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowPayroll && (
                            <Route
                                path="/misc-work-details/:miscWorkId"
                                element={<ProtectedRoute component={MiscWorkDetails} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowResources && (
                            <Route
                                path="/partner/:imKey"
                                element={<ProtectedRoute component={PartnerDetail} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowResources && (
                            <Route
                                path="/work-pool/:id"
                                element={<ProtectedRoute component={WorkPoolDetail} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowClients && (
                            <Route
                                path="/clients"
                                element={<ProtectedRoute component={Clients} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowSessions && (
                            <Route
                                path="/sessions"
                                element={<ProtectedRoute component={Sessions} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowSessions && (
                            <Route
                                path="/session/:sessionId"
                                element={<ProtectedRoute component={SessionDetail} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowSessions && (
                            <Route
                                path="/session/:sessionId/:tabIndex"
                                element={<ProtectedRoute component={SessionDetail} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowSessions && (
                            <Route
                                path="/trainer-entry/:id/:sessionId"
                                element={<ProtectedRoute component={TrainerEntryDetail} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowUsers && (
                            <Route path="/users" element={<ProtectedRoute component={Users} />} />
                        )}
                        {allowedRoutes.routeFlags.allowUserDetails && (
                            <Route
                                path="/user"
                                element={<ProtectedRoute component={UserDetail} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowLogs && (
                            <Route
                                path="/events"
                                element={<ProtectedRoute component={AccessLogs} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowMyAccess && (
                            <Route
                                path="/myaccess"
                                element={<ProtectedRoute component={MyAccess} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowDocuments && (
                            <Route
                                path="/documents"
                                element={<ProtectedRoute component={Documents} />}
                            />
                        )}
                        {allowedRoutes.routeFlags.allowHome && (
                            <Route path="/home" element={<ProtectedRoute component={Home} />} />
                        )}
                        <Route path="/" element={getRootPathElement()} />
                    </Routes>
                </FeatureFlagProvider>
            </ThemeProvider>
        </LocalizationProvider>
    );
};
